import React, { useContext, useEffect, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { currencyFormat } from "../../components/utils/utils";
import { SellerListingsDialog } from "../SellerListings/SellerListings";
import { CHECKOUT_TABLE_MIN_WIDTH, CheckoutItem, checkoutTableRowSx } from "./CheckoutItem";
import { CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import fedexGroundLogo from "../../components/Layout/AuthLayout/fedex-ground.svg";
import fedexExpressLogo from "../../components/Layout/AuthLayout/fedex-express.png";
import { useQueryShippingRates } from "../../queries/shipping/hooks/useQueryShippingRates";
import { BYPASS_FEDEX_RATE_CHECK, fedexShippingMap, getShippingRateRequestData, isRefrigerated } from "../../constants/shipping";
import { contextObject } from "../../components/shared/ShoppingCart/ShoppingCartContext";
import { NotificationType } from "../../constants/global";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import Button from "@mui/material/Button";

const SHIPPING_METHOD_ORDER = ["fedex-ground", "fedex-2-day", "fedex-priority-overnight"];

const shippingLogoFor = (methodKey: string) =>
  methodKey === "fedex-ground" ? fedexGroundLogo : fedexExpressLogo;

interface ICheckoutGroupItemProps {
  name: string;
  groupItem: any;
  shippingMethodsData: any;
  getGroupData: any;
  cartGroupData: any;
  setCartGroupData: any;
  setConfirmDisable: any;
  setFedexError: any;
  fedexError: any;
  freePriorityShippingApplied: boolean;
}
export const CheckoutGroupItem: React.FunctionComponent<ICheckoutGroupItemProps> = ({
  name,
  groupItem,
  shippingMethodsData,
  getGroupData,
  cartGroupData,
  setCartGroupData,
  setConfirmDisable,
  setFedexError,
  fedexError,
  freePriorityShippingApplied,
}) => {
  const { addToast } = useContext(ToasterContext);
  const { cart } = useContext(contextObject);
  const sellerUserId = groupItem[0]?.item?.User?.id;
  const sellerListingsAvailable = sellerUserId != null && !Number.isNaN(Number(sellerUserId));
  const [listingsOpen, setListingsOpen] = useState(false);
  const excludeListingIds = useMemo(
    () => groupItem.map((c: any) => Number(c.item.id)).filter((pid: number) => Number.isFinite(pid) && pid > 0),
    [groupItem]
  );
  const isFrozen = groupItem.find((cartItem: any) => cartItem.item.isFrozen);
  const priorityMethodKey =
    shippingMethodsData?.find((item: any) => item.key === "fedex-priority-overnight")?.key
    ?? shippingMethodsData[2]?.key;
  const defaultShippingMethod = isFrozen
    ? priorityMethodKey
    : (shippingMethodsData?.find((item: any) => item.key === "fedex-ground")?.key ?? shippingMethodsData[0].key);
  const [shippingMethod, setShippingMethod] = useState<any>(defaultShippingMethod);

  const [getShippingRates, { data: shippingRatesData, loading: shippingRatesLoading }] = useQueryShippingRates({
    fetchPolicy: 'no-cache'
  });

  const ratesReady = BYPASS_FEDEX_RATE_CHECK || (!!shippingRatesData && !shippingRatesLoading);

  const getCartItemTotal = (items: any) => {
    let total = 0;
    items.forEach((item: any) => {
      total = total + item.quantity * Number(item.item.price.replace(',', ''))
    });

    return total;
  }

  const getShippingSurcharge = (methodKey: string = shippingMethod) => {
    switch (methodKey) {
      case 'fedex-ground':
        return 7;
      case 'fedex-2-day':
        return 12;
      case 'fedex-priority-overnight':
        return 20;
      default:
        return 0;
    }
  }

  const getFedexNetCharge = (shippingRatesData: any) => {
    const baseCharge = (!BYPASS_FEDEX_RATE_CHECK
      && shippingRatesData?.getShippingRates?.output?.rateReplyDetails
      && shippingRatesData.getShippingRates.output.rateReplyDetails[0].ratedShipmentDetails)
      ? shippingRatesData.getShippingRates.output.rateReplyDetails[0].ratedShipmentDetails[0].totalNetFedExCharge
      : 0;

    // Fixed surcharges stay in place even when FedEx rate check is bypassed
    return baseCharge + getShippingSurcharge();
  }

  const getCartTotal = (fedexNet: any, cartItemTotal: any) => {
    return fedexNet + cartItemTotal
  }

  useEffect(() => {
    if (BYPASS_FEDEX_RATE_CHECK) {
      return;
    }
    getShippingRates({
      variables: {
        shippingRates: getShippingRateRequestData(
          groupItem[0].item.Pharmacy,
          groupItem[0].user.PharmacyUsers[0].Pharmacy,
          isFrozen ? 'PRIORITY_OVERNIGHT' : fedexShippingMap[shippingMethod],
          groupItem.length
        )
      },
    })
  }, [getShippingRates, shippingMethod, isFrozen, cart]);

  useEffect(() => {
    if (BYPASS_FEDEX_RATE_CHECK) {
      delete fedexError[name];
      getGroupData({
        total: +getFedexNetCharge(null) + getCartItemTotal(groupItem),
        fee: +getFedexNetCharge(null),
        key: `${groupItem[0].item.Pharmacy.legalName}--${groupItem[0].item.Pharmacy.id}`,
        shippingMethod: shippingMethodsData.find((item: any) => item.key === shippingMethod),
        groupItem: groupItem,
        pharmacyId: groupItem[0].item.Pharmacy.id,
      });
      return;
    }

    if (shippingRatesData) {
      if (!shippingRatesData.getShippingRates?.output?.rateReplyDetails && shippingRatesData.getShippingRates?.output?.alerts.length > 0) {
        setFedexError({
          ...fedexError,
          [name]: true
        })
        addToast({ type: NotificationType.Error, title: 'Something went wrong, please contact to "support@primepostrx.com".' });
      } else {
        delete fedexError[name];
      }
      getGroupData({
        total: +getFedexNetCharge(shippingRatesData) + getCartItemTotal(groupItem),
        fee: +getFedexNetCharge(shippingRatesData),
        key: `${groupItem[0].item.Pharmacy.legalName}--${groupItem[0].item.Pharmacy.id}`,
        shippingMethod: shippingMethodsData.find((item: any) => item.key === shippingMethod),
        groupItem: groupItem,
        pharmacyId: groupItem[0].item.Pharmacy.id,
      });
    }
  }, [shippingRatesData, shippingMethod, cart]);

  useEffect(() => {
    setConfirmDisable(BYPASS_FEDEX_RATE_CHECK ? false : shippingRatesLoading);
  }, [shippingRatesLoading]);

  useEffect(() => {
    if (isRefrigerated(groupItem) && shippingMethod !== "fedex-priority-overnight" && priorityMethodKey) {
      setShippingMethod(priorityMethodKey);
    }
  }, [groupItem, shippingMethod, priorityMethodKey]);



  const columnHeaders = [
    "Product Name",
    "NDC Number",
    "Strength",
    "Exp Date",
    "Seal Details",
    "Pack Type",
    "Quantity",
    "Per Unit",
    "Price",
    "",
  ];
  const itemSubtotal = getCartItemTotal(groupItem);
  const shippingFree = shippingMethod === "fedex-ground" || freePriorityShippingApplied;
  const shippingAmount = ratesReady ? (shippingFree ? 0 : getFedexNetCharge(shippingRatesData)) : null;
  const groupSubtotal = ratesReady
    ? (shippingFree ? itemSubtotal : getCartTotal(+getFedexNetCharge(shippingRatesData), itemSubtotal))
    : null;
  const refrigerated = isRefrigerated(groupItem);
  const ratesBusy = !BYPASS_FEDEX_RATE_CHECK && shippingRatesLoading;
  const orderedShippingMethods = SHIPPING_METHOD_ORDER
    .map((key) => shippingMethodsData.find((item: any) => item.key === key))
    .filter(Boolean);

  return (
    <Paper
      key={name}
      elevation={0}
      sx={{
        mb: 2.5,
        display: "flex",
        flexDirection: "column",
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.75,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          bgcolor: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ fontSize: 13, color: "#64748b" }}>
          {groupItem.length} {groupItem.length === 1 ? "listing" : "listings"}
        </Box>
        {sellerListingsAvailable && (
          <Button
            variant="contained"
            size="small"
            onClick={() => setListingsOpen(true)}
            sx={{
              bgcolor: "grey.900",
              color: "common.white",
              boxShadow: "none",
              fontWeight: 600,
              textTransform: "none",
              flexShrink: 0,
              "&:hover": {
                bgcolor: "grey.800",
                boxShadow: "none",
              },
            }}
          >
            Other listing from this Seller
          </Button>
        )}
      </Box>
      {sellerListingsAvailable && (
        <SellerListingsDialog
          open={listingsOpen}
          onClose={() => setListingsOpen(false)}
          sellerUserId={Number(sellerUserId)}
          excludeIds={excludeListingIds}
        />
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: CHECKOUT_TABLE_MIN_WIDTH }}>
          <Box
            sx={{
              ...checkoutTableRowSx,
              borderBottom: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
              "& > .checkout-cell": {
                ...((checkoutTableRowSx as any)["& > .checkout-cell"]),
                py: 1,
                alignItems: "center",
              },
            }}
          >
            {columnHeaders.map((header) => (
              <Box
                key={header || "actions"}
                className="checkout-cell"
                aria-label={header || "Remove"}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#64748b",
                  lineHeight: 1.3,
                }}
              >
                {header}
              </Box>
            ))}
          </Box>
          {groupItem.map((cartItem: any) => (
            <CheckoutItem
              key={cartItem.id}
              cartItem={cartItem}
              cartGroupData={cartGroupData}
              setCartGroupData={setCartGroupData}
              shippingRatesLoading={!BYPASS_FEDEX_RATE_CHECK && shippingRatesLoading}
            />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          p: 2,
        }}
      >
        <Box sx={{ flex: "0 1 auto", width: "auto", maxWidth: "100%", minWidth: 0 }}>
          <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", mb: 1 }}>
            Shipping
          </Box>
          <Box
            role="radiogroup"
            aria-label="Shipping method"
            id={`shippingMethod-${groupItem[0]?.item?.Pharmacy?.id ?? name}`}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1.5,
              width: "auto",
            }}
          >
            {orderedShippingMethods.map((method: any) => {
              const selected = shippingMethod === method.key;
              const restricted = refrigerated && method.key !== "fedex-priority-overnight";

              return (
                <Box
                  key={method.id}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-disabled={restricted || ratesBusy}
                  onClick={() => {
                    if (restricted || ratesBusy) {
                      return;
                    }
                    setShippingMethod(method.key);
                  }}
                  sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: 0.5,
                    width: 170,
                    maxWidth: "100%",
                    height: 72,
                    flex: "0 0 auto",
                    boxSizing: "border-box",
                    m: 0,
                    p: 1.5,
                    pr: 3.5,
                    textAlign: "left",
                    font: "inherit",
                    cursor: restricted ? "not-allowed" : "pointer",
                    opacity: restricted ? 0.5 : 1,
                    borderRadius: 1.5,
                    border: selected ? "2px solid #2563eb" : "1px solid #e2e8f0",
                    bgcolor: selected ? "rgba(239, 246, 255, 0.2)" : "#fff",
                    ...(!selected && !restricted
                      ? { "&:hover": { borderColor: "#cbd5e1" } }
                      : {}),
                  }}
                >
                  {selected && (
                    <CheckCircleIcon
                      sx={{ position: "absolute", top: 8, right: 8, fontSize: 16, color: "#2563eb" }}
                    />
                  )}
                  <Box
                    component="img"
                    src={shippingLogoFor(method.key)}
                    alt=""
                    sx={{ height: 18.4, width: "auto", maxWidth: "calc(100% - 18px)", objectFit: "contain", objectPosition: "left center" }}
                  />
                  <Box sx={{ width: "100%", fontSize: 13, fontWeight: 600, lineHeight: 1.25, color: "#0f172a", wordBreak: "break-word" }}>
                    {method.name}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {refrigerated && (
            <Box sx={{ mt: 1, fontSize: 12, lineHeight: 1.4, color: "#475569" }}>
              Refrigerated items require Priority Overnight shipping.
            </Box>
          )}
        </Box>

        <Box sx={{ flex: "0 1 280px", width: "100%", maxWidth: 280, ml: "auto" }}>
          <Box sx={{ mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ color: "#475569" }}>Items</Box>
            <Box>{currencyFormat(itemSubtotal)}</Box>
          </Box>
          <Box sx={{ mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ color: "#475569" }}>Shipping</Box>
            {shippingAmount === null ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {freePriorityShippingApplied && shippingMethod !== "fedex-ground" && (
                  <Box sx={{ textDecoration: "line-through", opacity: 0.75 }}>
                    {currencyFormat(getFedexNetCharge(shippingRatesData))}
                  </Box>
                )}
                <Box>{currencyFormat(shippingAmount)}</Box>
              </Box>
            )}
          </Box>
          {freePriorityShippingApplied && (
            <Box sx={{ mb: 1, color: "#2e7d32", fontSize: 12, fontWeight: "bold", textAlign: "right" }}>
              Free Priority Shipping applied
            </Box>
          )}
          <Divider sx={{ borderColor: "#e2e8f0" }} />
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, fontWeight: 700 }}>
            <Box>Subtotal</Box>
            {groupSubtotal === null ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <Box>{currencyFormat(groupSubtotal)}</Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

export default CheckoutGroupItem;